/**
 * App-review feature flags — remote-config gates for surfaces that must be
 * hidden from the NATIVE iOS build during App Store review, flippable after
 * approval WITHOUT a resubmission (the flag file lives on the CDN, not in the
 * app bundle).
 *
 * Why: Apple rejects builds that reference "beta" anywhere in the UI or ship
 * an in-app store surface that isn't approved. So on the native app:
 *   - Beta badge + /release-notes: HIDDEN unless the remote flag turns them on.
 *   - Merch: HIDDEN for everyone unless the remote flag turns it on
 *     (admins always see it, so it can be QA'd before the public flip).
 * On web nothing changes: Beta + release notes always show; merch remains
 * admin-only until `show_merch` flips true.
 *
 * To flip after approval, edit the flag file and re-upload:
 *   echo '{"show_beta":true,"show_release_notes":true,"show_merch":false,"show_stories":false}' > /tmp/app_flags.json
 *   rclone copyto /tmp/app_flags.json r2:ahp-static-data/config/app_flags.json --s3-disable-checksum --s3-no-head
 */
import { useEffect, useState } from 'react';
import { isNativeApp } from '@/lib/platform';

export interface AppReviewFlags {
  /** Beta badge next to the wordmark. Always true on web. */
  showBeta: boolean;
  /** /release-notes page + "What's New" nav links. Always true on web. */
  showReleaseNotes: boolean;
  /** Merch store nav links (web + native). Admin visibility is handled by callers. */
  showMerch: boolean;
  /** Community stories (share-your-story page + nav + homepage tile).
   *  Hidden everywhere until the section is seeded with real content, so an
   *  empty wall doesn't read as "this platform has no users". Admins bypass
   *  at the call sites so they can seed it. */
  showStories: boolean;
  /** Donation surfaces (/donate, Support nav, Settings "Support us"). Always
   *  true on web — taking donations is fine. Always false on native: the
   *  remote `show_payments` key is ignored there, since flipping a payment
   *  surface on after App Review is itself a rejection risk. */
  showPayments: boolean;
  /** PAID PRODUCTS — anything we sell: the $1.99 Analyst scenario path,
   *  /price-plan, credit packs. Off everywhere (2026-08-30) until the paid
   *  flow is finished and reviewed; donations are unaffected. Flip
   *  `show_paid_products: true` remotely to re-enable. */
  showPaidProducts: boolean;
  /** Dignity Chat (/chat + nav links). Hidden everywhere for launch — the
   *  LLM backend isn't live yet (blocked on a server-side Anthropic key), so
   *  the chat would answer with a holding message. Flip `show_chat` remotely
   *  once /api/chat is real. Admins bypass at the route for QA. */
  showChat: boolean;
}

const FLAGS_URL = 'https://static.americanhealthprices.com/config/app_flags.json';

function defaults(): AppReviewFlags {
  const native = isNativeApp();
  // Native starts fully hidden (safe for review even if the fetch fails);
  // web starts with current behavior (beta visible, merch admin-only).
  return { showBeta: !native, showReleaseNotes: !native, showMerch: false, showStories: false, showPayments: !native, showPaidProducts: false, showChat: false };
}

let cached: AppReviewFlags | null = null;
let inflight: Promise<AppReviewFlags> | null = null;

async function fetchFlags(): Promise<AppReviewFlags> {
  const base = defaults();
  try {
    const res = await fetch(`${FLAGS_URL}?t=${Math.floor(Date.now() / 60000)}`, { cache: 'no-store' });
    if (!res.ok) return base;
    const j = await res.json();
    return {
      // Web never hides beta/release-notes regardless of the file.
      showBeta: isNativeApp() ? j.show_beta === true : true,
      showReleaseNotes: isNativeApp() ? j.show_release_notes === true : true,
      showMerch: j.show_merch === true,
      showStories: j.show_stories === true,
      // Web payments are always fine. Native NEVER shows donation surfaces,
      // whatever the file says: turning on after review a payment flow
      // the reviewer never saw breaks guideline 2.3.1 / 3.1.1 (Master,
      // 2026-09-25: hide Support in the app for now).
      showPayments: !isNativeApp(),
      // Paid products stay off until the flow is finished and reviewed
      // (2026-08-30 — donations are fine; selling a product is not).
      showPaidProducts: j.show_paid_products === true,
      showChat: j.show_chat === true,
    };
  } catch {
    return base;
  }
}

/** Read the app-review flags. Returns safe defaults synchronously, then
 *  updates once the remote config loads (cached for the session). */
export function useAppReviewFlags(): AppReviewFlags {
  const [flags, setFlags] = useState<AppReviewFlags>(cached ?? defaults());
  useEffect(() => {
    if (cached) return;
    if (!inflight) {
      inflight = fetchFlags().then((f) => { cached = f; return f; });
    }
    let alive = true;
    inflight.then((f) => { if (alive) setFlags(f); });
    return () => { alive = false; };
  }, []);
  return flags;
}
